import { useContext, useEffect, useState } from "react";
import { Context } from "../../../context/Context";
import { Button, NoticeBox, Search } from "../../ui";
import { fetchAiImportSourceAPI } from "../../../utils/api/menuAiImport";
import { aiImportText } from "./aiImportText";

const columnName = (index) => {
  let name = "";
  let n = index;
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
};

// Spreadsheets: the cells exactly as the backend extracted them.
function SheetView({ sheets, t }) {
  const [index, setIndex] = useState(0);
  const sheet = sheets[index] || sheets[0];
  if (!sheet) return null;
  const columns = Array.from({ length: sheet.columnCount }, (_, i) => i + 1);
  return (
    <div className="ai-import-sheet">
      {sheets.length > 1 && (
        <div className="ai-import-sheet-tabs" role="tablist" aria-label={t.sheet}>
          {sheets.map((s, i) => (
            <Button key={s.name} type="button" role="tab" aria-selected={i === index} variant="primary" appearance={i === index ? "filled" : "soft"} size="xs" rounded onClick={() => setIndex(i)}>
              {s.name}
            </Button>
          ))}
        </div>
      )}
      <div className="ai-import-sheet-scroll" tabIndex={0} aria-label={`${t.sheet} ${sheet.name}`}>
        <table className="ai-import-sheet-table" dir="ltr">
          <thead>
            <tr>
              <th scope="col" aria-label="#" />
              {columns.map((c) => (
                <th key={c} scope="col">
                  {columnName(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheet.rows.map((row) => {
              const byColumn = new Map(row.cells.map((cell) => [cell.c, cell]));
              return (
                <tr key={row.r}>
                  <th scope="row">{row.r}</th>
                  {columns.map((c) => {
                    const cell = byColumn.get(c);
                    return (
                      <td key={c} dir="auto" className={cell?.bold ? "is-bold" : ""}>
                        {cell?.v || ""}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Original upload next to the draft: image (zoomable) or PDF from the
// authorized /source endpoint; spreadsheets from the extracted cells.
export default function AiImportSourceViewer({ draft }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const type = draft.source.sourceType;
  const [state, setState] = useState({ url: "", loading: type !== "excel", error: false });
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (type === "excel") return undefined;
    let alive = true;
    let url = "";
    fetchAiImportSourceAPI(draft._id)
      .then((blob) => {
        url = URL.createObjectURL(blob);
        if (alive) setState({ url, loading: false, error: false });
      })
      .catch(() => alive && setState({ url: "", loading: false, error: true }));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [draft._id, type]);

  if (type === "excel") return draft.extracted?.sheets?.length ? <SheetView sheets={draft.extracted.sheets} t={t} /> : <p className="menu-note">{t.sourceUnavailable}</p>;
  if (state.loading) return <p className="menu-note">{t.loadingSource}</p>;
  if (state.error || !state.url) return <NoticeBox variant="warning" size="sm">{t.sourceUnavailable}</NoticeBox>;
  if (type === "pdf") return <iframe className="ai-import-source-pdf" src={state.url} title={`${t.source}: ${draft.source.fileName}`} />;
  return (
    <div className={`ai-import-source-image ${zoomed ? "is-zoomed" : ""}`}>
      <Button type="button" variant="primary" appearance="soft" size="xs" rounded icon={<Search size={14} />} aria-pressed={zoomed} onClick={() => setZoomed((z) => !z)}>
        {zoomed ? "−" : "+"}
      </Button>
      <img src={state.url} alt={`${t.source}: ${draft.source.fileName}`} />
    </div>
  );
}
